// Interruption parsing at the boundary (FR-2.5): a refused turn request's
// typed failure → the interruption kind the transcript shows. The durable
// kinds (stopped, recorded errors, unfinished turns) are parsed from the log
// in conversationAdapter; this is the client-only half. Pure, total.

import { exhaustSwitchCase } from "@bilda/utils";
import type { SystemEntryKind } from "../components/Chat/types.js";
import type {
    DailyCeilingReached,
    ExternalError,
    UsageLimitReached,
} from "./externalError.js";

// A refused request never reached the turn, so nothing of it was recorded.
export const interruptionOfRefusal = (e: ExternalError): SystemEntryKind => {
    switch (e._tag) {
        case "External.Unreachable":
        case "External.AgentUnreachable":
            return { type: "connection_lost", messageSaved: false };
        case "External.Unauthorized":
            return { type: "signed_out" };
        case "External.QuotaExhausted":
            return { type: "budget_used_up" };
        case "External.UsageLimitReached":
        case "External.DailyCeilingReached":
            return limitKind(e);
        case "External.Unknown":
            return { type: "unknown", cause: e.detail ?? e._tag };
        case "External.Forbidden":
        case "External.PrivacyTermsNotAccepted":
        case "External.RateLimited":
        case "External.Rejected":
        case "External.Refused":
        case "External.Upstream":
        case "External.Malformed":
        case "External.AgentAuthMissing":
        case "External.AgentBusy":
        case "External.AgentFailed":
            return { type: "failed", error: e };
        default:
            return exhaustSwitchCase(e);
    }
};

// A usage limit reads the same whether the request was refused or the log
// recorded it (conversationAdapter).
export const usageLimitKind = (e: UsageLimitReached): SystemEntryKind =>
    e.resetsOn === undefined
        ? { type: "usage_limit_reached" }
        : { type: "usage_limit_reached", resetsOn: e.resetsOn };

// Either limit, refused or recorded, reads as its own notice.
export const limitKind = (
    e: UsageLimitReached | DailyCeilingReached,
): SystemEntryKind =>
    e._tag === "External.UsageLimitReached"
        ? usageLimitKind(e)
        : { type: "daily_ceiling_reached", reached: e.reached };
